import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-finance',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './finance.component.html',
  styleUrl: './finance.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class FinanceComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/fintech/fintech1.png',
      title: 'Retirement Plan Innovation ',
      description: 'This case study highlights how Kalpita Technologies transformed a mid-sized financial advisory firm’s retirement planning and pricing strategies through automation and advanced analytics. By integrating OCR and Azure Cognitive Services, the firm reduced report generation time by 50% and improved data accuracy by 30%, providing clients with clearer insights and competitive benchmarking against industry standards. The solution enhanced operational efficiency and client satisfaction, enabling advisors to deliver higher-value, data-driven recommendations.'
    }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Automated Billing Solution for Insurance Efficiency',
    //   description: 'Kalpita Technologies developed a scalable automated billing system integrated with Azure cloud functionalities to overcome manual errors, billing inconsistencies, and delayed notifications faced by a top-tier insurance company. The system supports multiple billing cycles, handles cancellations and renewals, and offers a user-friendly interface for real-time billing status management. This solution enhanced 100% accuracy and customer engagement through proactive alerts and transparent billing history, transforming the billing process into an efficient, error-free operation that meets the evolving needs of the insurance industry.'
    // },
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Unified Customer Engagement Portal',
    //   description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    // }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  FinanceFaqs: FaqItem[] = [
    {
      question: ' How to build a fintech app with security and compliance?',
      answer: `We follow industry-standard security frameworks, encrypt transactions, and 
      ensure compliance with regulations like PCI-DSS, GDPR, AML, and KYC.`
    },
    {
      question: 'Do you support integration with core banking and third-party systems?',
      answer: `Yes, we enable seamless integration with banks, payment gateways, credit bureaus, 
      and financial APIs.`
    },
    {
      question: 'What technologies do you use for fintech software development?',
      answer: `We leverage Microsoft Stack, cloud-native architecture, AI/ML, blockchain, and 
      secure API frameworks.`
    },
    {
      question: 'Can you customize fintech solutions for different financial products?',
      answer: `Absolutely, including insurance, lending, digital wallets, and neo-banking platforms.`
    },
    {
      question: ' What is AI-accelerated software development?',
      answer: `AI-accelerated development uses artificial intelligence to speed up architecture design, 
      code generation, testing, and integration - reducing development time by 50-60% while maintaining 
      high quality standards. This approach allows businesses to launch new capabilities in weeks 
      or months rather than years.`
    },
    {
      question: 'How quickly can you deliver a custom Fintech solution?',
      answer: `Timelines vary by project scope, but our AI-accelerated approach typically 
      delivers production-ready solutions in 6-9 months for comprehensive platforms - 60% faster 
      than traditional development. We provide realistic timelines during initial consultation 
      based on your specific requirements.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  scheduleDemo(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals

    // MODIFIED: Only start auto-slide if there is more than 1 slide
    if (this.caseStudies.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, this.SLIDE_DURATION);
    }
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;
    }
  }

  goToSlide(index: number): void {
    if (this.caseStudies.length <= 1) return;
    this.currentSlide = index;
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const fintechSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/fintech#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/fintech",
          "name": "Fintech Software Development | AI-Powered Financial Solutions | Kalpita Technologies",
          "description": "Future-ready fintech software development solutions by Kalpita Technologies. Secure, scalable, and AI-powered fintech applications with PCI-DSS, GDPR, AML, KYC compliance.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/fintech#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "Fintech", "item": "https://www.kalpitatechnologies.com/industries/fintech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/fintech#service",
          "name": "Fintech Software Development Solutions",
          "serviceType": "Fintech Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "Future-ready fintech software development delivering secure, scalable, and AI-powered applications for financial institutions."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/fintech#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How to build a fintech app with security and compliance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We follow industry-standard security frameworks, encrypt transactions, and ensure compliance with regulations like PCI-DSS, GDPR, AML, and KYC."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fintechSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  onCardClick(index: number): void {
    console.log('Clicked card', index);
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
